import React, { useState } from 'react';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { API } from '~/constants';
import { get, getApi, hasFunctionContactPhone, map, SiteRouterKey } from '~/utils';
import Intl from '@fx-ui/intl';
import { ExtValues, TEMPLATE_FILE_NAME } from '~/interfaces';
import ImportUploadPanel from '../import-upload-panel';
import ImportFailDetail from '../import-fail-detail';
import ImportMemberTip from '../import-member-tip';
import { useStores } from '~/hooks';
import siteRouterManager from '~/utils/site-router';
import { getMemberFieldColumnWidth, getMemberInviteErrMsg, trackImport } from '../../utils';
import { type ImportInfo, type ImportUploadResult, useImportMember } from '../../hooks';

interface IImportMemberPanelProps {
    onImportSuccess: (successCount?: number) => void;
}

interface ICanceledMember {
    corp_id: string;
    dept: string;
    email: string;
    err: { code: number | string; message: string };
    nickname: string;
    phone: string;
    username: string;
    ext?: ExtValues; // 预设字段
}

const ImportMemberPanel: React.FunctionComponent<IImportMemberPanelProps> = (
    props: IImportMemberPanelProps
) => {
    const { onImportSuccess } = props;

    const { corpUserStore, memberFieldStore } = useStores();

    const templateUrl = siteRouterManager.getTemplateFileSource(
        TEMPLATE_FILE_NAME.CONTACTS_BULK_IMPORT_TEMPLATE,
        SiteRouterKey.AdminTemplateFile,
        corpUserStore.tenantId
    );

    const [importFailDetailVisible, setImportFailDetailVisible] = useState<boolean>(false);
    const { importMember, importInfo } = useImportMember<ImportInfo<ICanceledMember>>({
        importApi: getApi(API.corp.excel_process.import_member, corpUserStore.tenantId)
    });

    const getFailTableColumns = () => {
        const columns = [
            { fixed: true, title: Intl.get('name'), dataIndex: 'nickname', minWidth: 136 },
            { title: Intl.get('serial.number'), dataIndex: 'username', minWidth: 136 },
            { title: Intl.get('email'), dataIndex: 'email', minWidth: 180 },
            { title: Intl.get('department'), dataIndex: 'dept', minWidth: 92 },
            ...map(memberFieldStore.presetAndCustomFields, (field) => {
                return {
                    title: field.label,
                    dataIndex: field.key,
                    minWidth: getMemberFieldColumnWidth(field),
                    render: (_: unknown, rowData: ICanceledMember) => {
                        return get(rowData, ['ext', field.key]);
                    }
                };
            }),
            {
                fixed: true,
                title: Intl.get('trigger.log.failure.reason'),
                minWidth: 180,
                dataIndex: 'errorMsg',
                render: (_: unknown, rowData: ICanceledMember) => {
                    return (
                        <div className="cell-data-error">{getMemberInviteErrMsg(rowData.err)}</div>
                    );
                }
            }
        ];

        if (hasFunctionContactPhone()) {
            columns.splice(2, 0, {
                title: Intl.get('phone'),
                dataIndex: 'phone',
                minWidth: 136
            });
        }

        return columns;
    };

    const handleImport = (fileData: ImportUploadResult) => {
        return importMember(fileData).then(
            ({ failCount, importInfo: nextImportInfo, successCount }) => {
                onImportSuccess(successCount);
                trackImport(
                    [
                        'nickname',
                        'username',
                        'phone',
                        'email',
                        'dept',
                        ...map(memberFieldStore.presetAndCustomFields, 'key')
                    ],
                    nextImportInfo.members
                );
                return {
                    successCount,
                    failCount
                };
            }
        );
    };

    return (
        <div className="fx-import-member-panel">
            <ImportMemberTip
                tips={[
                    Intl.getHTML('fxp.base.bg.intra_org.download_template_modify_import.tip', {
                        link: Intl.get('contact.template'),
                        Link: (s) => (
                            <Hyperlink compact underline={false} target="_self" href={templateUrl}>
                                {s}
                            </Hyperlink>
                        )
                    }),
                    Intl.get('contact.template.import.tip', { num: 200 })
                ]}
            />
            <ImportUploadPanel
                className="upload-panel"
                uploadPolicyUrl={getApi(API.corp.get_member_excel_token, corpUserStore.tenantId)}
                onImport={handleImport}
                onViewFailDetail={() => setImportFailDetailVisible(true)}
            />
            <ImportFailDetail
                visible={importFailDetailVisible}
                onClose={() => setImportFailDetailVisible(false)}
                token={importInfo?.token}
                exportApi={getApi(API.corp.excel_process.error_export, corpUserStore.tenantId)}
                columns={getFailTableColumns()}
                tableData={importInfo?.canceled || []}
            />
        </div>
    );
};

export default ImportMemberPanel;
